/**
 * WorkflowDetail Page
 *
 * Displays workflow details with option to edit or run.
 */
import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { AgentDetailSkeleton } from '../components/common/LoadingSkeleton';
import { RunWorkflowModal } from '../components/workflow/RunWorkflowModal';
import {
  useWorkflow,
  useDeleteWorkflow,
  useActivateWorkflow,
  usePauseWorkflow,
  useWorkflowExecutionsForWorkflow,
  getWorkflowStatusColor,
  getWorkflowStatusIcon,
  getExecutionModeIcon,
  getExecutionModeLabel,
  getTriggerTypeIcon,
  getTriggerTypeLabel,
} from '../hooks/useWorkflows';
import { WorkflowExecution } from '../services/workflow';

export default function WorkflowDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showRunModal, setShowRunModal] = useState(false);

  const { data: workflow, isLoading, error } = useWorkflow(id);
  const deleteWorkflow = useDeleteWorkflow();
  const activateWorkflow = useActivateWorkflow();
  const pauseWorkflow = usePauseWorkflow();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-4xl mx-auto py-8">
          <AgentDetailSkeleton />
        </div>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-4xl mx-auto py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">❌</div>
              <CardTitle className="mb-2">Workflow Not Found</CardTitle>
              <CardDescription className="mb-4">
                The workflow you're looking for doesn't exist or has been deleted.
              </CardDescription>
              <Button onClick={() => navigate('/workflows')}>
                Back to Workflows
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleToggleStatus = () => {
    if (workflow.status === 'ACTIVE') {
      pauseWorkflow.mutate(workflow.id);
    } else if (workflow.status === 'PAUSED' || workflow.status === 'DRAFT') {
      activateWorkflow.mutate(workflow.id);
    }
  };

  const handleRun = () => {
    setShowRunModal(true);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this workflow?')) {
      deleteWorkflow.mutate(workflow.id, {
        onSuccess: () => navigate('/workflows'),
      });
    }
  };

  const successRate =
    workflow.runCount > 0
      ? Math.round((workflow.successCount / workflow.runCount) * 100)
      : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-4xl mx-auto py-8">
        {/* Back Link */}
        <div className="mb-6">
          <Link
            to="/workflows"
            className="text-sm text-primary hover:underline"
          >
            ← Back to Workflows
          </Link>
        </div>

        {/* Header Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="text-4xl">
                  {getWorkflowStatusIcon(workflow.status)}
                </div>
                <div>
                  <CardTitle className="text-2xl">{workflow.name}</CardTitle>
                  <div className="flex items-center gap-2 mt-2">
                    <span
                      className={`px-3 py-1 text-sm rounded-full font-medium ${getWorkflowStatusColor(
                        workflow.status
                      )}`}
                    >
                      {workflow.status}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      v{workflow.version}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleRun}
                  disabled={workflow.status !== 'ACTIVE'}
                >
                  Run Workflow
                </Button>
                <Button variant="outline" asChild>
                  <Link to={`/workflows/${workflow.id}/edit`}>Edit</Link>
                </Button>
              </div>
            </div>
          </CardHeader>
          {workflow.description && (
            <CardContent>
              <p className="text-muted-foreground">{workflow.description}</p>
            </CardContent>
          )}
        </Card>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">
                  {getExecutionModeIcon(workflow.executionMode)}
                </span>
                <div>
                  <p className="text-xs text-muted-foreground">Execution Mode</p>
                  <p className="font-medium">
                    {getExecutionModeLabel(workflow.executionMode)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">
                  {getTriggerTypeIcon(workflow.trigger.type)}
                </span>
                <div>
                  <p className="text-xs text-muted-foreground">Trigger</p>
                  <p className="font-medium">
                    {getTriggerTypeLabel(workflow.trigger.type)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📦</span>
                <div>
                  <p className="text-xs text-muted-foreground">Steps</p>
                  <p className="font-medium">{workflow.steps.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🚀</span>
                <div>
                  <p className="text-xs text-muted-foreground">Total Runs</p>
                  <p className="font-medium">{workflow.runCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Success Rate */}
        {successRate !== null && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Success Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        successRate >= 80
                          ? 'bg-green-500'
                          : successRate >= 50
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${successRate}%` }}
                    />
                  </div>
                </div>
                <span
                  className={`text-lg font-bold ${
                    successRate >= 80
                      ? 'text-green-600'
                      : successRate >= 50
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                >
                  {successRate}%
                </span>
              </div>
              <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                <span>Success: {workflow.successCount}</span>
                <span>Failures: {workflow.failureCount}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Steps */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Workflow Steps</CardTitle>
            <CardDescription>
              Steps will be executed in {getExecutionModeLabel(workflow.executionMode).toLowerCase()} order
            </CardDescription>
          </CardHeader>
          <CardContent>
            {workflow.steps.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No steps configured</p>
                <Button variant="outline" className="mt-4" asChild>
                  <Link to={`/workflows/${workflow.id}/edit`}>Add Steps</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {workflow.steps
                  .sort((a, b) => a.order - b.order)
                  .map((step, index) => (
                    <div
                      key={step.id}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{step.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Agent: {step.agentName}
                        </p>
                      </div>
                      {step.conditions && step.conditions.length > 0 && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {step.conditions.length} condition{step.conditions.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tags */}
        {workflow.tags.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {workflow.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-muted-foreground">Created</dt>
                <dd className="font-medium">
                  {new Date(workflow.createdAt).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Updated</dt>
                <dd className="font-medium">
                  {new Date(workflow.updatedAt).toLocaleString()}
                </dd>
              </div>
              {workflow.lastRunAt && (
                <div>
                  <dt className="text-muted-foreground">Last Run</dt>
                  <dd className="font-medium">
                    {new Date(workflow.lastRunAt).toLocaleString()}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-muted-foreground">ID</dt>
                <dd className="font-mono text-xs">{workflow.id}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Recent Executions */}
        <RecentExecutions workflowId={workflow.id} />

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={handleToggleStatus}
                disabled={
                  workflow.status === 'ARCHIVED' ||
                  activateWorkflow.isPending ||
                  pauseWorkflow.isPending
                }
              >
                {workflow.status === 'ACTIVE' ? 'Pause Workflow' : 'Activate Workflow'}
              </Button>
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={deleteWorkflow.isPending}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                {deleteWorkflow.isPending ? 'Deleting...' : 'Delete Workflow'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Run Workflow Modal */}
      <RunWorkflowModal
        isOpen={showRunModal}
        onClose={() => setShowRunModal(false)}
        workflowId={workflow.id}
        workflowName={workflow.name}
      />
    </div>
  );
}

/**
 * Recent Executions Component
 */
function RecentExecutions({ workflowId }: { workflowId: string }) {
  const { data, isLoading } = useWorkflowExecutionsForWorkflow(workflowId, { limit: 5 });
  const executions = data?.data || [];

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getStatusConfig = (status: WorkflowExecution['status']) => {
    const configs: Record<string, { icon: string; color: string }> = {
      PENDING: { icon: '⏳', color: 'bg-yellow-100 text-yellow-800' },
      RUNNING: { icon: '🔄', color: 'bg-blue-100 text-blue-800' },
      COMPLETED: { icon: '✅', color: 'bg-green-100 text-green-800' },
      FAILED: { icon: '❌', color: 'bg-red-100 text-red-800' },
      CANCELLED: { icon: '🚫', color: 'bg-gray-100 text-gray-800' },
      PAUSED: { icon: '⏸️', color: 'bg-orange-100 text-orange-800' },
      // TIMEOUT is set by the stuck-execution reaper. Without it here a killed
      // run fell through to PENDING and read as "still going".
      TIMEOUT: { icon: '⏰', color: 'bg-red-100 text-red-800' },
      SKIPPED: { icon: '⏭️', color: 'bg-slate-100 text-slate-700' },
    };
    // Not `|| configs.PENDING`: an unknown status is not a pending one, and a
    // fallback that asserts a specific state hides the next missing enum value.
    return configs[status] || { icon: '❔', color: 'bg-gray-100 text-gray-800' };
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Recent Executions</CardTitle>
            <CardDescription>
              Latest execution history for this workflow
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/executions?workflowId=${workflowId}`}>View All</Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-12 bg-slate-100 rounded animate-pulse"
              />
            ))}
          </div>
        ) : executions.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-8">
            No executions yet. Run the workflow to see execution history.
          </p>
        ) : (
          <div className="space-y-2">
            {executions.map((execution) => {
              const statusConfig = getStatusConfig(execution.status);
              return (
                <Link
                  key={execution.id}
                  to={`/workflows/${workflowId}/executions/${execution.id}`}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{statusConfig.icon}</span>
                    <div>
                      <p className="text-sm font-medium">
                        {formatDate(execution.startedAt || execution.createdAt)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Duration: {formatDuration(execution.durationMs)}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded-full font-medium ${statusConfig.color}`}
                  >
                    {execution.status}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
